/**
 * Percentage Calculator — pure math core.
 *
 * Every function returns `number | null` (or an object | null). `null` means the
 * inputs don't yield a defined result (non-finite input, or a divide-by-zero),
 * so the UI can render a hint instead of `NaN` / `Infinity`. Results are returned
 * at full precision; rounding for display happens in the UI via `roundTo`.
 */

/** True only when every argument is a finite number. */
function finite(...nums: number[]): boolean {
  return nums.every((n) => typeof n === "number" && Number.isFinite(n));
}

/** Round to `decimals` places, trimming binary-float noise (e.g. 0.1+0.2). */
export function roundTo(value: number, decimals = 2): number {
  if (!finite(value)) return value;
  const f = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * f) / f;
}

/** What is `percent`% of `value`? → (percent / 100) × value */
export function percentOf(percent: number, value: number): number | null {
  if (!finite(percent, value)) return null;
  return (percent / 100) * value;
}

/** `part` is what percent of `whole`? → (part / whole) × 100 */
export function whatPercent(part: number, whole: number): number | null {
  if (!finite(part, whole) || whole === 0) return null;
  return (part / whole) * 100;
}

/** `part` is `percent`% of what? → part / (percent / 100) */
export function findWhole(part: number, percent: number): number | null {
  if (!finite(part, percent) || percent === 0) return null;
  return part / (percent / 100);
}

/**
 * Signed percentage change from `oldValue` to `newValue`.
 * → ((newValue − oldValue) / oldValue) × 100  (positive = increase, negative = decrease)
 * Also powers the "percentage increase" page (an increase is a positive change).
 */
export function percentChange(
  oldValue: number,
  newValue: number,
): number | null {
  if (!finite(oldValue, newValue) || oldValue === 0) return null;
  return ((newValue - oldValue) / oldValue) * 100;
}

/**
 * Magnitude of a percentage decrease from `oldValue` to `newValue`.
 * → ((oldValue − newValue) / oldValue) × 100  (positive when newValue < oldValue)
 */
export function percentDecrease(
  oldValue: number,
  newValue: number,
): number | null {
  if (!finite(oldValue, newValue) || oldValue === 0) return null;
  return ((oldValue - newValue) / oldValue) * 100;
}

/**
 * Percentage difference between two values (order-independent).
 * → (|a − b| / ((a + b) / 2)) × 100. Undefined when a + b === 0.
 */
export function percentDifference(a: number, b: number): number | null {
  if (!finite(a, b)) return null;
  const avg = (a + b) / 2;
  if (avg === 0) return null;
  return (Math.abs(a - b) / Math.abs(avg)) * 100;
}

export interface TipResult {
  tipAmount: number;
  total: number;
  perPerson: number;
}

/**
 * Tip calculator. `split` is the number of people sharing the bill (default 1).
 * Returns tip amount, grand total, and per-person share.
 */
export function tip(bill: number, tipPct: number, split = 1): TipResult | null {
  if (!finite(bill, tipPct, split) || split < 1) return null;
  const tipAmount = (tipPct / 100) * bill;
  const total = bill + tipAmount;
  return { tipAmount, total, perPerson: total / split };
}

export interface DiscountResult {
  saved: number;
  finalPrice: number;
}

/**
 * Discount / sale-price calculator.
 * → saved = price × (off / 100); finalPrice = price − saved.
 */
export function discount(price: number, offPct: number): DiscountResult | null {
  if (!finite(price, offPct)) return null;
  const saved = (offPct / 100) * price;
  return { saved, finalPrice: price - saved };
}
